'use client';

import React, { useState } from 'react';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { Input } from 'src/components/PulseUI/Input';
import { extractErrorMessage } from 'src/components/Strategy/Profiles/utils/errorUtils';

export interface RenameRouteDialogProps {
  isOpen: boolean;
  currentLabel: string;
  isSaving: boolean;
  onSave: (label: string) => Promise<void>;
  onClose: () => void;
}

const RenameRouteDialog: React.FC<RenameRouteDialogProps> = ({
  isOpen,
  currentLabel,
  isSaving,
  onSave,
  onClose,
}) => {
  const [label, setLabel] = useState(currentLabel);
  const [error, setError] = useState<string | null>(null);

  const trimmed = label.trim();
  const canSave = trimmed.length > 0 && trimmed !== currentLabel && !isSaving;

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);
    try {
      await onSave(trimmed);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "Couldn't rename this route."));
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="rename-route-dialog-title"
      data-testid="rename-route-dialog"
    >
      <Dialog.Header>
        <h2 className="text-lg font-medium" id="rename-route-dialog-title">
          Rename Route
        </h2>
      </Dialog.Header>
      <Dialog.Content>
        <div className="flex flex-col gap-2 p-4">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={255}
            autoFocus
            aria-label="Route name"
            data-testid="rename-route-input"
          />
          {error && (
            <p className="text-xs text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          variant="outline"
          onClick={onClose}
          disabled={isSaving}
          className="w-full rounded-full"
          data-testid="rename-route-cancel"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleSave}
          disabled={!canSave}
          loading={isSaving}
          className="w-full rounded-full"
          data-testid="rename-route-save"
        >
          Save
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default RenameRouteDialog;
